import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Box, Typography, Button, Card, CardContent, Grid, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, Paper, IconButton, Dialog, DialogTitle, DialogContent,
  DialogActions, TextField, MenuItem, Chip, Alert, LinearProgress, CircularProgress } from '@mui/material';
import { Add, Edit, Delete, ArrowBack } from '@mui/icons-material';
import api from '../../services/api';

const statusLabels = {
  erfasst: { label: 'Erfasst', color: 'default' },
  geprueft: { label: 'Geprüft', color: 'info' },
  freigegeben: { label: 'Freigegeben', color: 'success' },
};

const belegartLabels = {
  rechnung: 'Rechnung',
  quittung: 'Quittung',
  honorar: 'Honorar',
  reisekosten: 'Reisekosten',
  sonstiges: 'Sonstiges',
};

const FinanzenPage = () => {
  const { projektId } = useParams();
  const navigate = useNavigate();

  const [uebersicht, setUebersicht] = useState(null);
  const [kostenpositionen, setKostenpositionen] = useState([]);
  const [belege, setBelege] = useState([]);
  const [error, setError] = useState('');
  const [projektTitel, setProjektTitel] = useState('');

  // Filter
  const [filterKP, setFilterKP] = useState('');
  const [filterStatus, setFilterStatus] = useState('');

  // Dialoge
  const [kpOpen, setKpOpen] = useState(false);
  const [kpEditing, setKpEditing] = useState(null);
  const [kpForm, setKpForm] = useState({ bezeichnung: '', sollBetrag: '' });

  const [belegOpen, setBelegOpen] = useState(false);
  const [belegEditing, setBelegEditing] = useState(null);
  const [belegForm, setBelegForm] = useState({
    datum: '', beschreibung: '', empfaenger: '', betrag: '',
    kostenpositionId: '', belegart: 'rechnung', notizen: ''
  });

  const load = useCallback(async () => {
    try {
      const [uRes, kpRes, bRes, pRes] = await Promise.all([
        api.get(`/finanzen/projekt/${projektId}/uebersicht`),
        api.get(`/finanzen/projekt/${projektId}/kostenpositionen`),
        api.get(`/finanzen/projekt/${projektId}/belege`),
        api.get(`/projekte/${projektId}`)
      ]);
      setUebersicht(uRes.data);
      setKostenpositionen(kpRes.data);
      setBelege(bRes.data);
      setProjektTitel(pRes.data.titel);
    } catch (err) { setError('Fehler beim Laden der Finanzdaten'); }
  }, [projektId]);

  useEffect(() => { load(); }, [load]);

  const formatCurrency = (val) => parseFloat(val || 0).toLocaleString('de-DE', { style: 'currency', currency: 'EUR' });

  const getVerbrauchColor = (pct) => {
    if (pct > 95) return '#D32F2F';
    if (pct >= 80) return '#F57C00';
    return '#1B5E20';
  };

  // Kostenposition handlers
  const handleKpOpen = (kp = null) => {
    if (kp) {
      setKpEditing(kp);
      setKpForm({ bezeichnung: kp.bezeichnung, sollBetrag: kp.sollBetrag || '' });
    } else {
      setKpEditing(null);
      setKpForm({ bezeichnung: '', sollBetrag: '' });
    }
    setKpOpen(true);
  };

  const handleKpSave = async () => {
    try {
      if (kpEditing) {
        await api.put(`/finanzen/kostenpositionen/${kpEditing.id}`, kpForm);
      } else {
        await api.post(`/finanzen/projekt/${projektId}/kostenpositionen`, kpForm);
      }
      setKpOpen(false);
      load();
    } catch (err) { setError('Fehler beim Speichern der Kostenposition'); }
  };

  const handleKpDelete = async (id) => {
    if (window.confirm('Kostenposition wirklich löschen?')) {
      try {
        await api.delete(`/finanzen/kostenpositionen/${id}`);
        load();
      } catch (err) { setError('Fehler beim Löschen'); }
    }
  };

  // Beleg handlers
  const handleBelegOpen = (beleg = null) => {
    if (beleg) {
      setBelegEditing(beleg);
      setBelegForm({
        datum: beleg.datum || '', beschreibung: beleg.beschreibung || '',
        empfaenger: beleg.empfaenger || '', betrag: beleg.betrag || '',
        kostenpositionId: beleg.kostenpositionId || '', belegart: beleg.belegart || 'rechnung',
        notizen: beleg.notizen || ''
      });
    } else {
      setBelegEditing(null);
      setBelegForm({ datum: '', beschreibung: '', empfaenger: '', betrag: '',
        kostenpositionId: '', belegart: 'rechnung', notizen: '' });
    }
    setBelegOpen(true);
  };

  const handleBelegSave = async () => {
    try {
      const data = { ...belegForm };
      if (!data.kostenpositionId) delete data.kostenpositionId;
      if (!data.empfaenger) delete data.empfaenger;
      if (!data.notizen) delete data.notizen;

      if (belegEditing) {
        await api.put(`/finanzen/belege/${belegEditing.id}`, data);
      } else {
        await api.post(`/finanzen/projekt/${projektId}/belege`, data);
      }
      setBelegOpen(false);
      load();
    } catch (err) { setError('Fehler beim Speichern des Belegs'); }
  };

  const handleBelegDelete = async (id) => {
    if (window.confirm('Beleg wirklich löschen?')) {
      try {
        await api.delete(`/finanzen/belege/${id}`);
        load();
      } catch (err) { setError('Fehler beim Löschen'); }
    }
  };

  // Gefilterte Belege
  const filteredBelege = belege.filter(b => {
    if (filterKP && b.kostenpositionId !== filterKP) return false;
    if (filterStatus && b.status !== filterStatus) return false;
    return true;
  });

  if (!uebersicht) return <Box sx={{ p: 3 }}><Typography>Laden...</Typography></Box>;

  const summeSoll = uebersicht.positionen.reduce((s, p) => s + p.sollBetrag, 0);
  const summeIst = uebersicht.positionen.reduce((s, p) => s + p.istBetrag, 0);

  return (
    <Box>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
        <IconButton onClick={() => navigate(`/projekte/${projektId}`)} sx={{ color: '#1B5E20' }}>
          <ArrowBack />
        </IconButton>
        <Box sx={{ flexGrow: 1 }}>
          <Typography variant="h4" fontWeight="bold" color="#1B5E20">Finanzen</Typography>
          <Typography variant="body2" color="text.secondary">{projektTitel}</Typography>
        </Box>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}

      {/* Übersicht-Cards */}
      <Grid container spacing={2} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ bgcolor: '#E8F5E9' }}>
            <CardContent>
              <Typography variant="caption" color="text.secondary">Gesamtbudget</Typography>
              <Typography variant="h5" fontWeight="bold" color="#1B5E20">
                {formatCurrency(uebersicht.gesamtbudget)}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography variant="caption" color="text.secondary">Ausgaben gesamt</Typography>
              <Typography variant="h5" fontWeight="bold">
                {formatCurrency(uebersicht.istGesamt)}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography variant="caption" color="text.secondary">Verbleibend</Typography>
              <Typography variant="h5" fontWeight="bold" color={uebersicht.rest < 0 ? '#D32F2F' : '#1B5E20'}>
                {formatCurrency(uebersicht.rest)}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography variant="caption" color="text.secondary">Verbrauch</Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mt: 0.5 }}>
                <Box sx={{ position: 'relative', display: 'inline-flex' }}>
                  <CircularProgress variant="determinate" value={Math.min(uebersicht.verbrauchProzent, 100)}
                    size={48} thickness={5}
                    sx={{ color: getVerbrauchColor(uebersicht.verbrauchProzent) }} />
                  <Box sx={{ position: 'absolute', top: 0, left: 0, bottom: 0, right: 0,
                    display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <Typography variant="caption" fontWeight="bold">
                      {Math.round(uebersicht.verbrauchProzent)}%
                    </Typography>
                  </Box>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Soll-Ist-Tabelle */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h5" fontWeight="bold" color="#1B5E20">Kostenpositionen (Soll-Ist)</Typography>
        <Button variant="outlined" startIcon={<Add />} onClick={() => handleKpOpen()}
          sx={{ color: '#1B5E20', borderColor: '#1B5E20', '&:hover': { borderColor: '#2E7D32', bgcolor: '#E8F5E9' } }}>
          Neue Kostenposition
        </Button>
      </Box>
      <TableContainer component={Paper} sx={{ mb: 4 }}>
        <Table>
          <TableHead>
            <TableRow sx={{ bgcolor: '#E8F5E9' }}>
              <TableCell sx={{ fontWeight: 'bold' }}>Kostenposition</TableCell>
              <TableCell sx={{ fontWeight: 'bold' }} align="right">Soll (EUR)</TableCell>
              <TableCell sx={{ fontWeight: 'bold' }} align="right">Ist (EUR)</TableCell>
              <TableCell sx={{ fontWeight: 'bold' }} align="right">Differenz (EUR)</TableCell>
              <TableCell sx={{ fontWeight: 'bold', minWidth: 150 }}>Verbrauch</TableCell>
              <TableCell sx={{ fontWeight: 'bold' }} align="right">Aktionen</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {uebersicht.positionen.length === 0 ? (
              <TableRow><TableCell colSpan={6} align="center" sx={{ py: 4, color: '#999' }}>Noch keine Kostenpositionen vorhanden</TableCell></TableRow>
            ) : (
              <>
                {uebersicht.positionen.map((pos) => (
                  <TableRow key={pos.id} hover>
                    <TableCell>{pos.bezeichnung}</TableCell>
                    <TableCell align="right">{formatCurrency(pos.sollBetrag)}</TableCell>
                    <TableCell align="right">{formatCurrency(pos.istBetrag)}</TableCell>
                    <TableCell align="right" sx={{ color: pos.differenz < 0 ? '#D32F2F' : 'inherit' }}>
                      {formatCurrency(pos.differenz)}
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <LinearProgress variant="determinate" value={Math.min(pos.verbrauchProzent, 100)}
                          sx={{ flexGrow: 1, height: 8, borderRadius: 4, bgcolor: '#E8F5E9',
                            '& .MuiLinearProgress-bar': { bgcolor: getVerbrauchColor(pos.verbrauchProzent) } }} />
                        <Typography variant="caption" sx={{ minWidth: 40 }}>
                          {Math.round(pos.verbrauchProzent)}%
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell align="right">
                      <IconButton size="small" onClick={() => handleKpOpen(pos)} sx={{ color: '#1B5E20' }}><Edit /></IconButton>
                      <IconButton size="small" color="error" onClick={() => handleKpDelete(pos.id)}><Delete /></IconButton>
                    </TableCell>
                  </TableRow>
                ))}
                <TableRow sx={{ bgcolor: '#F5F5F5' }}>
                  <TableCell sx={{ fontWeight: 'bold' }}>Summe</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 'bold' }}>{formatCurrency(summeSoll)}</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 'bold' }}>{formatCurrency(summeIst)}</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 'bold', color: (summeSoll - summeIst) < 0 ? '#D32F2F' : 'inherit' }}>
                    {formatCurrency(summeSoll - summeIst)}
                  </TableCell>
                  <TableCell />
                  <TableCell />
                </TableRow>
              </>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Belegliste */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h5" fontWeight="bold" color="#1B5E20">Belege</Typography>
        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
          <TextField select size="small" label="Kostenposition" value={filterKP}
            onChange={(e) => setFilterKP(e.target.value)} sx={{ minWidth: 180 }}>
            <MenuItem value="">Alle</MenuItem>
            {kostenpositionen.map((kp) => <MenuItem key={kp.id} value={kp.id}>{kp.bezeichnung}</MenuItem>)}
          </TextField>
          <TextField select size="small" label="Status" value={filterStatus}
            onChange={(e) => setFilterStatus(e.target.value)} sx={{ minWidth: 140 }}>
            <MenuItem value="">Alle</MenuItem>
            {Object.entries(statusLabels).map(([key, val]) => <MenuItem key={key} value={key}>{val.label}</MenuItem>)}
          </TextField>
          <Button variant="outlined" startIcon={<Add />} onClick={() => handleBelegOpen()}
            sx={{ color: '#1B5E20', borderColor: '#1B5E20', '&:hover': { borderColor: '#2E7D32', bgcolor: '#E8F5E9' } }}>
            Neuer Beleg
          </Button>
        </Box>
      </Box>
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow sx={{ bgcolor: '#E8F5E9' }}>
              <TableCell sx={{ fontWeight: 'bold' }}>Beleg-Nr.</TableCell>
              <TableCell sx={{ fontWeight: 'bold' }}>Datum</TableCell>
              <TableCell sx={{ fontWeight: 'bold' }}>Beschreibung</TableCell>
              <TableCell sx={{ fontWeight: 'bold' }}>Empfänger</TableCell>
              <TableCell sx={{ fontWeight: 'bold' }}>Kostenposition</TableCell>
              <TableCell sx={{ fontWeight: 'bold' }} align="right">Betrag</TableCell>
              <TableCell sx={{ fontWeight: 'bold' }}>Status</TableCell>
              <TableCell sx={{ fontWeight: 'bold' }} align="right">Aktionen</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredBelege.length === 0 ? (
              <TableRow><TableCell colSpan={8} align="center" sx={{ py: 4, color: '#999' }}>Keine Belege vorhanden</TableCell></TableRow>
            ) : filteredBelege.map((b) => (
              <TableRow key={b.id} hover>
                <TableCell sx={{ fontFamily: 'monospace' }}>{b.belegnummer}</TableCell>
                <TableCell>{new Date(b.datum).toLocaleDateString('de-DE')}</TableCell>
                <TableCell>{b.beschreibung}</TableCell>
                <TableCell>{b.empfaenger || '-'}</TableCell>
                <TableCell>{b.kostenposition?.bezeichnung || '-'}</TableCell>
                <TableCell align="right" sx={{ fontWeight: 'bold' }}>{formatCurrency(b.betrag)}</TableCell>
                <TableCell>
                  <Chip label={statusLabels[b.status]?.label || b.status}
                    color={statusLabels[b.status]?.color || 'default'} size="small" />
                </TableCell>
                <TableCell align="right">
                  <IconButton size="small" onClick={() => handleBelegOpen(b)} sx={{ color: '#1B5E20' }}><Edit /></IconButton>
                  <IconButton size="small" color="error" onClick={() => handleBelegDelete(b.id)}><Delete /></IconButton>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Dialog: Kostenposition */}
      <Dialog open={kpOpen} onClose={() => setKpOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{kpEditing ? 'Kostenposition bearbeiten' : 'Neue Kostenposition'}</DialogTitle>
        <DialogContent>
          <TextField fullWidth label="Bezeichnung *" value={kpForm.bezeichnung}
            onChange={(e) => setKpForm({...kpForm, bezeichnung: e.target.value})} margin="normal" />
          <TextField fullWidth label="Soll-Betrag (EUR)" type="number" value={kpForm.sollBetrag}
            onChange={(e) => setKpForm({...kpForm, sollBetrag: e.target.value})} margin="normal" />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setKpOpen(false)}>Abbrechen</Button>
          <Button onClick={handleKpSave} variant="contained" sx={{ bgcolor: '#1B5E20' }}>Speichern</Button>
        </DialogActions>
      </Dialog>

      {/* Dialog: Beleg */}
      <Dialog open={belegOpen} onClose={() => setBelegOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>{belegEditing ? 'Beleg bearbeiten' : 'Neuer Beleg'}</DialogTitle>
        <DialogContent>
          <TextField fullWidth label="Datum *" type="date" value={belegForm.datum}
            onChange={(e) => setBelegForm({...belegForm, datum: e.target.value})} margin="normal"
            InputLabelProps={{ shrink: true }} />
          <TextField fullWidth label="Beschreibung *" value={belegForm.beschreibung}
            onChange={(e) => setBelegForm({...belegForm, beschreibung: e.target.value})} margin="normal" />
          <TextField fullWidth label="Empfänger" value={belegForm.empfaenger}
            onChange={(e) => setBelegForm({...belegForm, empfaenger: e.target.value})} margin="normal" />
          <TextField fullWidth label="Betrag (EUR) *" type="number" value={belegForm.betrag}
            onChange={(e) => setBelegForm({...belegForm, betrag: e.target.value})} margin="normal" />
          <TextField fullWidth select label="Kostenposition" value={belegForm.kostenpositionId}
            onChange={(e) => setBelegForm({...belegForm, kostenpositionId: e.target.value})} margin="normal">
            <MenuItem value="">Keine Zuordnung</MenuItem>
            {kostenpositionen.map((kp) => <MenuItem key={kp.id} value={kp.id}>{kp.bezeichnung}</MenuItem>)}
          </TextField>
          <TextField fullWidth select label="Belegart" value={belegForm.belegart}
            onChange={(e) => setBelegForm({...belegForm, belegart: e.target.value})} margin="normal">
            {Object.entries(belegartLabels).map(([key, label]) => <MenuItem key={key} value={key}>{label}</MenuItem>)}
          </TextField>
          {belegEditing && (
            <TextField fullWidth select label="Status" value={belegForm.status || belegEditing?.status}
              onChange={(e) => setBelegForm({...belegForm, status: e.target.value})} margin="normal">
              {Object.entries(statusLabels).map(([key, val]) => <MenuItem key={key} value={key}>{val.label}</MenuItem>)}
            </TextField>
          )}
          <TextField fullWidth label="Notizen" value={belegForm.notizen}
            onChange={(e) => setBelegForm({...belegForm, notizen: e.target.value})} margin="normal" multiline rows={2} />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setBelegOpen(false)}>Abbrechen</Button>
          <Button onClick={handleBelegSave} variant="contained" sx={{ bgcolor: '#1B5E20' }}>Speichern</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default FinanzenPage;
